import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Checkbox,
  CheckboxGroup,
  useDisclosure,
  Spinner,
  InputGroup,
  InputLeftElement,
  Icon,
  Select,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
  Tag,
  TagLabel,
  Tooltip,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiKey, FiUserPlus, FiShield } from 'react-icons/fi';

const API_BASE = '/api/v1/admin-ui';

function Users() {
  const toast = useToast();
  
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [userForm, setUserForm] = useState({ 
    name: '', 
    email: '', 
    username: '',
    password: '', 
    admin_type: '',
    active: true,
    blocked: false,
    tag_ids: [],
  });
  const [passwordForm, setPasswordForm] = useState({ password: '', confirm_password: '' });
  const [userToPassword, setUserToPassword] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToPromote, setUserToPromote] = useState(null);
  
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isPasswordModalOpen, onOpen: onPasswordModalOpen, onClose: onPasswordModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isPromoteModalOpen, onOpen: onPromoteModalOpen, onClose: onPromoteModalClose } = useDisclosure();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'X-Tenant-ID': localStorage.getItem('instance_id'),
  });

  // Fetch available tags
  const fetchTags = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/profile-tags/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setAvailableTags(data.tags || []);
      }
    } catch (e) {
      console.error('Error loading tags:', e);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/stats/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  }, []);

  // Fetch users and tags
  useEffect(() => {
    fetchUsers();
    fetchTags();
    fetchStats();
  }, [fetchTags, fetchStats]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/users/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setUsers(data.users || []);
      } else {
        toast({ title: data.message || 'Failed to load users', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error loading users', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  // Filter users by search term
  const filteredUsers = users.filter(user => 
    (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.username || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // User Modal Handlers
  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ 
      name: '', 
      email: '', 
      username: '',
      password: '', 
      admin_type: '',
      active: true,
      blocked: false,
      tag_ids: [],
    });
    onUserModalOpen();
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({ 
      name: user.name || '', 
      email: user.email || '', 
      username: user.username || '',
      password: '', 
      admin_type: user.admin_type || '',
      active: user.active !== false,
      blocked: user.blocked || false,
      tag_ids: (user.tags || []).map(t => t.id),
    });
    onUserModalOpen();
  };

  const handleSaveUser = async () => {
    const method = editingUser ? 'PUT' : 'POST';
    const url = editingUser ? `${API_BASE}/users/${editingUser.id}/` : `${API_BASE}/users/`;
    
    const payload = { ...userForm };
    if (!payload.password) delete payload.password;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: editingUser ? 'User updated' : 'User created', status: 'success', duration: 3000 });
        onUserModalClose();
        fetchUsers();
      } else {
        toast({ title: data.message || 'Error saving user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving user', status: 'error', duration: 3000 });
    }
  };

  // Password reset
  const openPasswordModal = (user) => {
    setUserToPassword(user);
    setPasswordForm({ password: '', confirm_password: '' });
    onPasswordModalOpen();
  };

  const handleSetPassword = async () => {
    if (passwordForm.password !== passwordForm.confirm_password) {
      toast({ title: 'Passwords do not match', status: 'error', duration: 3000 });
      return;
    }
    if (passwordForm.password.length < 8) {
      toast({ title: 'Password must be at least 8 characters', status: 'error', duration: 3000 });
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/users/${userToPassword.id}/set-password/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password: passwordForm.password }),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Password updated', status: 'success', duration: 3000 });
        onPasswordModalClose();
      } else {
        toast({ title: data.message || 'Error setting password', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error setting password', status: 'error', duration: 3000 });
    }
  };

  // Delete user
  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteModalOpen();
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/users/${userToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'User deleted', status: 'success', duration: 3000 });
        fetchUsers();
      } else {
        toast({ title: data.message || 'Error deleting user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting user', status: 'error', duration: 3000 });
    }
    onDeleteModalClose();
    setUserToDelete(null);
  };

  // Promote to super admin
  const openPromoteModal = (user) => {
    setUserToPromote(user);
    onPromoteModalOpen();
  };

  const handlePromoteUser = async () => {
    if (!userToPromote) return;
    try {
      const res = await fetch(`${API_BASE}/users/${userToPromote.id}/promote-super-admin/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'User promoted to Super Admin', status: 'success', duration: 3000 });
        fetchUsers();
      } else {
        toast({ title: data.message || 'Error promoting user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error promoting user', status: 'error', duration: 3000 });
    }
    onPromoteModalClose();
    setUserToPromote(null);
  };

  const getAdminTypeBadge = (adminType) => {
    const colors = {
      'system_administrator': 'red',
      'account_admin': 'purple',
      'event_host': 'blue',
      'event_instance_host': 'cyan',
      'course_tutor': 'green',
    };
    const labels = {
      'system_administrator': 'System Admin',
      'account_admin': 'Account Admin',
      'event_host': 'Event Host',
      'event_instance_host': 'Instance Host',
      'course_tutor': 'Tutor',
    };
    return adminType ? (
      <Badge colorScheme={colors[adminType] || 'gray'}>
        {labels[adminType] || adminType}
      </Badge>
    ) : null;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4}>Loading users...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Users & Profiles</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddUser}>
          Add User
        </Button>
      </HStack>

      {/* Summary Cards */}
      {stats && (
        <HStack spacing={6} mb={6} wrap="wrap">
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" minW="200px" textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color="orange.500">{stats.total_users}</Text>
            <Text fontSize="sm" color="gray.600">Total Users</Text>
          </Box>
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" minW="200px" textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color="green.500">{stats.active_profiles}</Text>
            <Text fontSize="sm" color="gray.600">Active Profiles</Text>
          </Box>
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" minW="200px" textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color="purple.500">{stats.super_admins}</Text>
            <Text fontSize="sm" color="gray.600">Super Admins</Text>
          </Box>
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" minW="200px" textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color="blue.500">{stats.profile_tags}</Text>
            <Text fontSize="sm" color="gray.600">Profile Tags</Text>
          </Box>
        </HStack>
      )}

      {/* Search */}
      <InputGroup mb={6} maxW="400px">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by name, email, or username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          focusBorderColor="orange.500"
        />
      </InputGroup>

      {/* Users Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Username</Th>
              <Th>Tags</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredUsers.map(user => (
              <Tr key={user.id}>
                <Td fontWeight="medium">{user.name || '-'}</Td>
                <Td>{user.email || '-'}</Td>
                <Td>{user.username || '-'}</Td>
                <Td>
                  <Wrap spacing={1} maxW="200px">
                    {(user.tags || []).map(tag => (
                      <WrapItem key={tag.id}>
                        <Tag size="sm" colorScheme="orange" variant="subtle">
                          <TagLabel>{tag.name}</TagLabel>
                        </Tag>
                      </WrapItem>
                    ))}
                    {(!user.tags || user.tags.length === 0) && (
                      <Text fontSize="sm" color="gray.400">-</Text>
                    )}
                  </Wrap>
                </Td>
                <Td>
                  {user.is_superuser && <Badge colorScheme="red" mr={2}>Super Admin</Badge>}
                  {getAdminTypeBadge(user.admin_type)}
                </Td>
                <Td>
                  {user.active !== false ? (
                    <Badge colorScheme="green">Active</Badge>
                  ) : (
                    <Badge colorScheme="gray">Inactive</Badge>
                  )}
                  {user.blocked && <Badge colorScheme="red" ml={2}>Blocked</Badge>}
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => openEditUser(user)}
                      aria-label="Edit user"
                    />
                    <IconButton
                      icon={<FiKey />}
                      size="sm"
                      variant="ghost"
                      colorScheme="orange"
                      onClick={() => openPasswordModal(user)}
                      aria-label="Set password"
                    />
                    {!user.is_superuser && (
                      <IconButton
                        icon={<FiShield />}
                        size="sm"
                        variant="ghost"
                        colorScheme="purple"
                        onClick={() => openPromoteModal(user)}
                        aria-label="Promote to Super Admin"
                      />
                    )}
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDeleteUser(user)}
                      aria-label="Delete user"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
            {filteredUsers.length === 0 && (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8}>
                  <Text color="gray.500">No users found</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* User Modal */}
      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingUser ? 'Edit User' : 'Add User'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Username</FormLabel>
                <Input
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
              {!editingUser && (
                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    focusBorderColor="orange.500"
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Admin Type</FormLabel>
                <Select
                  value={userForm.admin_type}
                  onChange={(e) => setUserForm({ ...userForm, admin_type: e.target.value })}
                  focusBorderColor="orange.500"
                  placeholder="Select admin type (optional)"
                >
                  <option value="system_administrator">System Administrator</option>
                  <option value="account_admin">Account Admin</option>
                  <option value="event_host">Event Host</option>
                  <option value="event_instance_host">Event Instance Host</option>
                  <option value="course_tutor">Course Tutor</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Tags</FormLabel>
                {availableTags.length > 0 ? (
                  <Wrap spacing={2}>
                    {availableTags.map(tag => (
                      <WrapItem key={tag.id}>
                        <Checkbox
                          isChecked={userForm.tag_ids.includes(tag.id)}
                          onChange={(e) => {
                            const newTagIds = e.target.checked
                              ? [...userForm.tag_ids, tag.id]
                              : userForm.tag_ids.filter(id => id !== tag.id);
                            setUserForm({ ...userForm, tag_ids: newTagIds });
                          }}
                          colorScheme="orange"
                        >
                          <Tag size="sm" colorScheme="orange" variant={userForm.tag_ids.includes(tag.id) ? 'solid' : 'outline'}>
                            <TagLabel>{tag.name}</TagLabel>
                          </Tag>
                        </Checkbox>
                      </WrapItem>
                    ))}
                  </Wrap>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No tags available. Create tags in the Profile Tags section.
                  </Text>
                )}
              </FormControl>
              <HStack width="full" spacing={6}>
                <Checkbox
                  isChecked={userForm.active}
                  onChange={(e) => setUserForm({ ...userForm, active: e.target.checked })}
                  colorScheme="orange"
                >
                  Active
                </Checkbox>
                <Checkbox
                  isChecked={userForm.blocked}
                  onChange={(e) => setUserForm({ ...userForm, blocked: e.target.checked })}
                  colorScheme="red"
                >
                  Blocked
                </Checkbox>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUserModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveUser}>
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Password Modal */}
      <Modal isOpen={isPasswordModalOpen} onClose={onPasswordModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Set Password for {userToPassword?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Confirm Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPasswordModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSetPassword}>Set Password</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This action cannot be undone.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteUser}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Promote to Super Admin Modal */}
      <Modal isOpen={isPromoteModalOpen} onClose={onPromoteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Promote to Super Admin</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              Are you sure you want to promote <strong>{userToPromote?.name}</strong> to Super Admin? 
              They will have full administrative access to this instance.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPromoteModalClose}>Cancel</Button>
            <Button colorScheme="purple" onClick={handlePromoteUser}>Promote</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Users;
